import { OnlineRequiredError, failureMessage, isRejection, statusOf } from './errors';
import { newOperationId as defaultNewOperationId } from './ids';
import type { OfflineStorage } from './ports';
import type {
  DeadLetter,
  FlushResult,
  MutationRequest,
  MutationResult,
  NewOutboxEntry,
  OutboxConfig,
  OutboxEntry,
  ReplayKeyField,
  Submitted,
} from './types';

/**
 * §28 / ADR-010 — the offline outbox, one implementation for every client.
 *
 * Every mutation is written to durable storage **before** the network is
 * touched, and each is idempotent through its replay key, so a half-succeeded
 * flush is safe to retry. The invariant — delivered, queued or dead-lettered,
 * never nowhere — is the whole point of the file; do not add a path that drops
 * an entry without one of those three outcomes. Online-required actions (card
 * payments, drawer open/close) must NOT go through the queue: they set
 * `onlineOnly` and surface their error.
 *
 * Storage, transport and reachability are injected, so this file imports
 * nothing from React Native, the DOM or axios.
 */
export interface Outbox {
  /** Everything still waiting, oldest first. */
  all(): Promise<OutboxEntry[]>;
  /** The rejected work waiting for a human decision, newest last. */
  dead(): Promise<DeadLetter[]>;
  enqueue(entry: NewOutboxEntry): Promise<void>;
  /** Drops the queue AND the dead letter. Only ever on an explicit instruction. */
  clear(): Promise<void>;
  /** Put a dead letter back at the head of the queue and try it again. */
  retryDead(operationId: string): Promise<void>;
  discardDead(operationId: string): Promise<void>;
  clearDead(): Promise<void>;
  submit<T = unknown>(
    entry: Omit<NewOutboxEntry, 'operationId'> & Partial<Pick<OutboxEntry, 'operationId'>>,
  ): Promise<Submitted<T>>;
  mutate<T>(request: MutationRequest): Promise<MutationResult<T>>;
  flush(): Promise<FlushResult>;
  /** The same generator the queue mints its own keys with. */
  newOperationId(): string;
}

export function createOutbox(config: OutboxConfig): Outbox {
  const storage: OfflineStorage = config.storage;
  const transport = config.transport;
  const namespace = config.namespace ?? 'venue.outbox';
  const KEY = namespace;
  const DEAD_KEY = `${namespace}.dead`;
  const MAX_ATTEMPTS = config.maxAttempts ?? 8;
  const DEFAULT_FIELD: ReplayKeyField = config.defaultReplayField ?? 'operationId';
  const HEADER = config.operationIdHeader ?? 'X-Operation-Id';
  const mintId = config.newOperationId ?? (() => defaultNewOperationId());
  const isReachable = config.isReachable ?? (async () => true);

  // ------------------------------------------------------------- transport

  /**
   * The body actually sent. `header` and `none` put nothing in it, because the
   * API's `forbidNonWhitelisted` pipe answers 400 to a field the DTO does not
   * declare — and a 400 dead-letters the entry.
   */
  function bodyFor(entry: Pick<OutboxEntry, 'body' | 'operationId' | 'replayField'>) {
    const base = (entry.body ?? {}) as Record<string, unknown>;
    const field = entry.replayField ?? DEFAULT_FIELD;
    return field === 'header' || field === 'none'
      ? { ...base }
      : { ...base, [field]: entry.operationId };
  }

  /**
   * The id travels as a header too, on every request but a `none` one:
   * `@OperationId()` on the server reads *only* `x-operation-id`, and that is
   * the sole replay guard on the routes that take no body at all. Headers are
   * not touched by the ValidationPipe, so sending it costs nothing on the
   * routes that ignore it — which is why the default is to always send it.
   */
  function headersFor(entry: Pick<OutboxEntry, 'operationId' | 'replayField'>) {
    if ((entry.replayField ?? DEFAULT_FIELD) === 'none') return {};
    return { [HEADER]: entry.operationId };
  }

  // ---------------------------------------------------------- serialisation

  /**
   * One promise chain for every read-modify-write of the two stored lists.
   *
   * A host flushes from a network listener AND an app-state listener, and a
   * person can tap a new mutation in the middle of either. Without this,
   * `enqueue()`'s read-modify-write and the flush loop's write-back interleave:
   * the order just taken is overwritten by a snapshot from before it existed
   * and is gone with no trace. Everything that mutates storage goes through
   * here, and the flush loop never writes a whole snapshot back — it removes
   * exactly the entry it just handled, so an entry queued mid-flush cannot be
   * clobbered.
   */
  let storageChain: Promise<unknown> = Promise.resolve();

  function locked<T>(work: () => Promise<T>): Promise<T> {
    const run = storageChain.then(work, work);
    // The chain must survive a failed critical section, or one throw wedges the
    // queue for the rest of the process.
    storageChain = run.then(
      () => undefined,
      () => undefined,
    );
    return run;
  }

  async function readQueue(): Promise<OutboxEntry[]> {
    const raw = await storage.getItem(KEY);
    return raw ? (JSON.parse(raw) as OutboxEntry[]) : [];
  }

  async function writeQueue(queue: OutboxEntry[]): Promise<void> {
    await storage.setItem(KEY, JSON.stringify(queue));
  }

  async function readDead(): Promise<DeadLetter[]> {
    const raw = await storage.getItem(DEAD_KEY);
    return raw ? (JSON.parse(raw) as DeadLetter[]) : [];
  }

  async function writeDead(letters: DeadLetter[]): Promise<void> {
    await storage.setItem(DEAD_KEY, JSON.stringify(letters));
  }

  async function removeFromQueue(operationId: string): Promise<void> {
    await locked(async () => {
      const queue = await readQueue();
      await writeQueue(queue.filter((item) => item.operationId !== operationId));
    });
  }

  /** Move one entry out of the queue and into the dead letter, atomically. */
  async function toDeadLetter(entry: OutboxEntry, error: unknown): Promise<void> {
    await locked(async () => {
      const queue = await readQueue();
      const stored = queue.find((item) => item.operationId === entry.operationId) ?? entry;
      await writeQueue(queue.filter((item) => item.operationId !== entry.operationId));
      const letters = await readDead();
      letters.push({
        entry: stored,
        status: statusOf(error),
        message: failureMessage(error),
        failedAt: Date.now(),
      });
      await writeDead(letters);
    });
  }

  async function bumpAttempts(operationId: string): Promise<number> {
    return locked(async () => {
      const queue = await readQueue();
      const entry = queue.find((item) => item.operationId === operationId);
      if (!entry) return 0;
      entry.attempts += 1;
      await writeQueue(queue);
      return entry.attempts;
    });
  }

  async function summarise(
    sent: number,
    rejected: number,
    results: Record<string, unknown>,
  ): Promise<FlushResult> {
    const [queue, letters] = await Promise.all([readQueue(), readDead()]);
    return { sent, remaining: queue.length, rejected, failed: letters.length, results };
  }

  // ---------------------------------------------------------------- flush

  let inFlight: Promise<FlushResult> | null = null;

  async function runFlush(): Promise<FlushResult> {
    const results: Record<string, unknown> = {};
    if (!(await isReachable())) return summarise(0, 0, results);

    // A snapshot decides what this run attempts; anything enqueued mid-run is
    // picked up by the next flush. The queue is only ever mutated one entry at
    // a time under the lock, so a concurrent enqueue is never lost.
    const snapshot = await readQueue();
    let sent = 0;
    let rejected = 0;

    for (const entry of snapshot) {
      try {
        const response = await transport({
          method: entry.method,
          url: entry.url,
          data: bodyFor(entry),
          headers: headersFor(entry),
        });
        results[entry.operationId] = response.data;
        await removeFromQueue(entry.operationId);
        sent += 1;
      } catch (error: any) {
        if (isRejection(error)) {
          // The server refused it on the merits. Retrying forever would block
          // every order behind it; dropping it would lose the person's work
          // silently — so it is parked where the banner can show it.
          await toDeadLetter(entry, error);
          rejected += 1;
          continue;
        }

        if (statusOf(error) === 401) {
          // Nothing will change until someone signs in again. Stop the run and
          // leave the queue exactly as it is — no attempt is spent on it.
          break;
        }

        const attempts = await bumpAttempts(entry.operationId);
        if (attempts >= MAX_ATTEMPTS) {
          await toDeadLetter(entry, error);
          rejected += 1;
          continue;
        }
        break;
      }
    }

    return summarise(sent, rejected, results);
  }

  const outbox: Outbox = {
    newOperationId: mintId,

    async all() {
      return readQueue();
    },

    async dead() {
      return readDead();
    },

    async enqueue(entry) {
      await locked(async () => {
        const queue = await readQueue();
        queue.push({ ...entry, createdAt: Date.now(), attempts: 0 });
        await writeQueue(queue);
      });
    },

    async clear() {
      await locked(async () => {
        await storage.removeItem(KEY);
        await storage.removeItem(DEAD_KEY);
      });
    },

    async retryDead(operationId) {
      await locked(async () => {
        const letters = await readDead();
        const letter = letters.find((item) => item.entry.operationId === operationId);
        if (!letter) return;
        await writeDead(letters.filter((item) => item.entry.operationId !== operationId));
        const queue = await readQueue();
        // Same `operationId`, so if the first attempt did land after all, the
        // server replays its original result rather than acting twice.
        queue.unshift({ ...letter.entry, attempts: 0 });
        await writeQueue(queue);
      });
    },

    async discardDead(operationId) {
      await locked(async () => {
        const letters = await readDead();
        await writeDead(letters.filter((item) => item.entry.operationId !== operationId));
      });
    },

    async clearDead() {
      await locked(async () => {
        await storage.removeItem(DEAD_KEY);
      });
    },

    /**
     * Queue a mutation and try to drain the whole queue immediately.
     *
     * The write is durable before the network is touched, so the caller never
     * loses it. `synced` says whether the queue actually drained: a flow that
     * needs the server-assigned id (open a check, then move a charge onto it) can
     * only continue when it did — offline, the create still lands later and the
     * UI says so rather than inventing an id. Because it drains in order, an
     * entry submitted behind stuck work stays behind it: ordering is what makes
     * a replay of the whole queue equivalent to what the person actually did.
     */
    async submit<T = unknown>(
      entry: Omit<NewOutboxEntry, 'operationId'> & Partial<Pick<OutboxEntry, 'operationId'>>,
    ): Promise<Submitted<T>> {
      const operationId = entry.operationId ?? mintId();
      await outbox.enqueue({ ...entry, operationId });

      let flushed = await outbox.flush();
      if (flushed.results[operationId] === undefined) {
        // A flush already running when we enqueued took its snapshot before
        // this entry existed, so it could not have sent it. One more run does.
        const queue = await readQueue();
        if (queue.some((item) => item.operationId === operationId)) {
          flushed = await outbox.flush();
        }
      }

      return {
        operationId,
        synced: flushed.results[operationId] !== undefined,
        data: flushed.results[operationId] as T | undefined,
        remaining: flushed.remaining,
      };
    },

    /**
     * Queue a mutation and send **that one entry** straight away.
     *
     * **Durable first.** The entry is on disk before the request is made, so an
     * app kill mid-request cannot lose it: the next launch flushes it, and the
     * replay key makes the double delivery a no-op. The request then goes
     * immediately so the person still gets the server's answer — the new order
     * id, the split's resulting checks, the updated balance — rather than a tap
     * that looks like it did nothing. Unlike {@link submit} it does not drain
     * the queue behind it, which lets a till answer instantly while older work
     * is stuck; the flush loop catches up on the next reconnect or foreground.
     */
    async mutate<T>(request: MutationRequest): Promise<MutationResult<T>> {
      const operationId = request.operationId ?? mintId();
      const entry: NewOutboxEntry = {
        operationId,
        method: request.method,
        url: request.url,
        body: request.body,
        replayField: request.replayField ?? DEFAULT_FIELD,
        label: request.label,
      };

      const reachable = await isReachable();
      if (!reachable && request.onlineOnly) throw new OnlineRequiredError();

      // `onlineOnly` work is never queued, so it is never written to disk.
      if (!request.onlineOnly) await outbox.enqueue(entry);

      if (!reachable) return { queued: true, data: null };

      try {
        const response = await transport({
          method: entry.method,
          url: entry.url,
          data: bodyFor(entry),
          headers: headersFor(entry),
        });
        if (!request.onlineOnly) await removeFromQueue(operationId);
        return { queued: false, data: response.data as T };
      } catch (error) {
        if (request.onlineOnly) throw error;
        if (isRejection(error)) {
          // The person is looking at the error right now, so this one does not
          // need a dead letter — but it must not stay queued either, or the
          // flush loop would re-send work they have already been told failed.
          await removeFromQueue(operationId);
          throw error;
        }
        return { queued: true, data: null };
      }
    },

    /**
     * Drain the queue in order, stopping at the first entry that cannot be sent.
     * Single-flight: two listeners firing at once share one run rather than
     * racing each other through the same entries.
     */
    flush(): Promise<FlushResult> {
      if (!inFlight) {
        inFlight = runFlush().finally(() => {
          inFlight = null;
        });
      }
      return inFlight;
    },
  };

  return outbox;
}
